import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

const API_BASE_URL = config.API_BASE_URL + '/api';

const useGameLogic = (selectedCategory, selectedDifficulty, questionCount, muted, playSound) => {
  const [gameState, setGameState] = useState('start');
  const [gameData, setGameData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMounted, setIsMounted] = useState(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Enhanced logging function
  const log = (level, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${level.toUpperCase()}] ${message}`;
    
    if (data) {
      console.log(logMessage, data);
    } else {
      console.log(logMessage);
    }
  };

  // Error handling wrapper
  const handleError = (error, context, fallbackMessage) => {
    log('error', `${context}: ${error.message || error}`, {
      error: error.response?.data || error,
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method
    });
    
    let userMessage = fallbackMessage;
    
    if (error.response?.status === 404) {
      userMessage = 'Game not found. Please start a new game.';
    } else if (error.response?.status === 401) {
      userMessage = 'Session expired. Please start a new game.';
    } else if (error.response?.status >= 500) {
      userMessage = 'Server error. Please try again later.';
    } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      userMessage = 'Cannot connect to server. Please check your connection.';
    } else if (error.response?.data?.error) {
      userMessage = error.response.data.error;
    }
    
    setError(userMessage);
    return userMessage;
  };

  const loadQuestion = useCallback(async (gameId, questionIndex) => {
    log('info', `Starting to load question ${questionIndex} for game ${gameId}`);
    
    // Don't show loading for subsequent questions to prevent flicker
    if (questionIndex === 0) {
      setLoading(true);
    }
    setError(null);
    
    try {
      const url = `${API_BASE_URL}/question/${gameId}/${questionIndex}`;
      log('info', `Making request to: ${url}`);
      
      const response = await axios.get(url);
      log('info', 'Question loaded successfully', {
        questionIndex,
        gameId,
        questionLength: response.data.question?.length,
        answersCount: response.data.answers?.length,
        totalQuestions: response.data.totalQuestions
      });
      
      if (isMounted) {
        // Update all state at once to prevent flicker
        setCurrentQuestion(response.data);
        setSelectedAnswer(null);
        setShowFeedback(false);
        setFeedback(null);
        setLoading(false);
        
        log('info', `Question ${questionIndex} state updated successfully`);
      }
    } catch (err) {
      handleError(err, 'Failed to load question', 'Failed to load question. Please try again.');
      setLoading(false);
    } finally {
      log('info', `Finished loading question ${questionIndex}`);
    }
  }, [isMounted]);

  const startGame = useCallback(async (gameId = null, token = null, playerName = null, mode = 'singlePlayer') => {
    log('info', 'Starting new game', {
      mode,
      questionCount,
      selectedCategory,
      selectedDifficulty,
      muted,
      gameId,
      playerName,
      token: token ? 'present' : 'missing'
    });
    
    setLoading(true);
    setError(null);
    
    try {
      let responseData;
      
      if (mode === 'singlePlayer') {
        const url = `${API_BASE_URL}/create-game`;
        const requestData = {
          questionCount: questionCount,
          category: selectedCategory,
          difficulty: selectedDifficulty
        };
        
        log('info', `Making request to: ${url}`, requestData);
        
        const response = await axios.post(url, requestData);
        responseData = response.data;
      } else {
        // Multiplayer mode - use provided gameId and token
        log('info', 'Multiplayer mode - checking game data', { gameId, token: token ? 'present' : 'missing' });
        if (!gameId || !token) {
          log('error', 'Missing game data for multiplayer mode', { gameId, token: token ? 'present' : 'missing' });
          throw new Error("Missing game data for multiplayer mode");
        }
        
        responseData = {
          gameId: gameId,
          token: token,
          userId: `user_${Math.random().toString(36).substring(2, 10)}`, // Generate a temporary userId
          questionCount: questionCount,
          category: selectedCategory,
          difficulty: selectedDifficulty
        };
        
        log('info', 'Using provided multiplayer game data', responseData);
      }
      
      log('info', 'Game created/started successfully', {
        gameId: responseData.gameId,
        questionCount: responseData.questionCount,
        hasToken: !!responseData.token,
        userId: responseData.userId,
        mode
      });
      
      if (isMounted) {
        setGameData(responseData);
        setGameState('playing');
        setCurrentQuestionIndex(0);
        setScore(0);
        
        log('info', 'Game state updated, transitioning to playing mode');
      }
      log('info', 'About to load first question...');
      
      await loadQuestion(responseData.gameId, 0);
      
      // Play start sound if not muted
      if (!muted) {
        log('info', 'Playing start sound');
        playSound('start');
      } else {
        log('info', 'Sound muted, skipping start sound');
      }
      
      log('info', 'Game started successfully');
    } catch (err) {
      handleError(err, 'Failed to start game', 'Failed to start game. Please try again.');
    } finally {
      setLoading(false);
      log('info', 'Start game process completed');
    }
  }, [questionCount, selectedCategory, selectedDifficulty, muted, playSound, loadQuestion, isMounted]);

  const submitAnswer = useCallback(async (answerIndex, multiplayerPlayerName = null) => {
    if (selectedAnswer !== null || showFeedback) {
      log('warn', 'Answer submission blocked', {
        selectedAnswer,
        showFeedback,
        reason: selectedAnswer !== null ? 'Answer already selected' : 'Feedback already shown'
      });
      return;
    }
    
    log('info', 'Submitting answer', {
      answerIndex,
      questionIndex: currentQuestionIndex,
      gameId: gameData?.gameId
    });
    
    setSelectedAnswer(answerIndex);
    setLoading(true);
    
    try {
      const url = `${API_BASE_URL}/submit-answer`;
      const requestData = {
        gameId: gameData.gameId,
        questionIndex: currentQuestionIndex,
        answerIndex: answerIndex,
        token: gameData.token,
        playerName: multiplayerPlayerName // Add player name for multiplayer
      };
      
      log('info', `Making request to: ${url}`, {
        gameId: gameData.gameId,
        questionIndex: currentQuestionIndex,
        answerIndex
      });
      
      const response = await axios.post(url, requestData);
      
      const isCorrect = response.data.correct;
      log('info', 'Answer submitted successfully', {
        isCorrect,
        correctAnswerIndex: response.data.correctAnswerIndex,
        currentScore: score,
        newScore: isCorrect ? score + 1 : score
      });
      
      if (isCorrect) {
        setScore(prev => prev + 1);
        log('info', 'Playing correct sound');
        playSound('correct');
      } else {
        log('info', 'Playing incorrect sound');
        playSound('incorrect');
      }
      
      setFeedback({
        correct: isCorrect,
        correctAnswerIndex: response.data.correctAnswerIndex,
        correctAnswer: response.data.correctAnswer
      });
      setShowFeedback(true);
      
      log('info', 'Feedback set, starting auto-advance timer');
      
      // Auto-advance to next question after 2 seconds (increased for better UX)
      setTimeout(() => {
        log('info', 'Auto-advance timer triggered', {
          currentQuestionIndex,
          totalQuestions: gameData.questionCount,
          isLastQuestion: currentQuestionIndex >= gameData.questionCount - 1
        });
        
        if (currentQuestionIndex < gameData.questionCount - 1) {
          const nextIndex = currentQuestionIndex + 1;
          log('info', `Advancing to question ${nextIndex}`);
          
          // Start loading state for smoother transition
          setLoading(true);
          
          // Preload the next question for smoother transition
          setCurrentQuestionIndex(nextIndex);
          
          // Small delay to allow the index update to render before loading
          setTimeout(() => {
            loadQuestion(gameData.gameId, nextIndex);
          }, 100);
        } else {
          log('info', 'Game completed, transitioning to finished state');
          setGameState('finished');
          if (!muted) {
            log('info', 'Playing completion sound');
            playSound('complete');
          }
        }
      }, 2000);
      
    } catch (err) {
      handleError(err, 'Failed to submit answer', 'Failed to submit answer. Please try again.');
    } finally {
      setLoading(false);
      log('info', 'Answer submission process completed');
    }
  }, [selectedAnswer, showFeedback, gameData, currentQuestionIndex, playSound, loadQuestion, score, muted]);

  const getAnswerButtonClass = useCallback((index) => {
    if (!showFeedback) return 'answer-button';
    
    if (index === selectedAnswer) {
      return feedback.correct ? 'answer-button correct' : 'answer-button incorrect';
    }
    
    if (index === feedback.correctAnswerIndex) {
      return 'answer-button correct-answer';
    }
    
    return 'answer-button';
  }, [showFeedback, selectedAnswer, feedback]);

  const resetGame = useCallback(() => {
    log('info', 'Resetting game to start screen');
    
    if (isMounted) {
      setGameState('start');
      setGameData(null);
      setCurrentQuestion(null);
      setCurrentQuestionIndex(0);
      setScore(0);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setFeedback(null);
      setLoading(false);
      setError(null);
      
      log('info', 'Game reset complete, returning to start screen');
    }
  }, [isMounted]);

  return {
    gameState,
    setGameState,
    gameData,
    currentQuestion,
    currentQuestionIndex,
    score,
    selectedAnswer,
    showFeedback,
    feedback,
    loading,
    error,
    setError,
    startGame,
    submitAnswer,
    getAnswerButtonClass,
    resetGame
  };
};

export default useGameLogic;
